<script setup>
import { computed, inject, ref } from "vue";
import { Tooltip } from "@/components/ui/tooltip";
import { POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";
import { paletteFor, spreadShades, formatPercent } from "@/composables/useChartUtils";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import AnimatedPercent from "./AnimatedPercent.vue";

const props = defineProps({
  data: { type: Array, required: true },
  choiceCodes: { type: Array, required: true },
  choiceLabels: { type: Object, required: true },
  horizontal: { type: Boolean, default: true },
  showSignificance: { type: Boolean, default: false },
  showNpsScore: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
});

const emit = defineEmits(["bar-click"]);

function onSegClick(event, seg, row) {
  if (!props.clickable || seg.code === "999" || row.questionId == null) return;
  event.stopPropagation();
  emit("bar-click", { questionId: row.questionId, code: seg.code, popId: row.popId });
}

// Segments grow from 0 when the card is revealed on scroll (view mode entrance animation)
const revealed = inject("resultsCardRevealed", ref(true));

function segSize(seg) {
  if (!revealed.value) return "0%";
  return `${Math.max(seg.value * 100, 0)}%`;
}

// Rows start growing one after the other (Appinio-style stagger), capped for long lists
function revealDelay(index) {
  return Math.min(index * 40, 400);
}

function revealDelayStyle(index) {
  return { transitionDelay: `${revealDelay(index)}ms` };
}

function getRowColors(popColor, count) {
  if (!count) return [];
  return spreadShades(paletteFor(popColor), count);
}

/**
 * For each row, build an array of segments with colors from that row's population palette.
 */
const rows = computed(() =>
  props.data.map((row) => {
    const colors = getRowColors(row.popColor, props.choiceCodes.length);
    const segments = [];
    let cumulative = 0;
    props.choiceCodes.forEach((code, i) => {
      const value = row[`choice_${code}`] || 0;
      const count = row[`count_${code}`];
      const label = row[`label_${code}`] || props.choiceLabels[code] || code;
      const significance = row[`sig_${code}`] || null;
      const significanceDetails = row[`sigDetails_${code}`] || null;
      segments.push({
        code,
        label,
        value,
        count,
        offset: cumulative,
        color: row.choiceColors?.[code] || colors[i],
        significance,
        significanceDetails,
        scores: row[`scores_${code}`] || null,
      });
      cumulative += value;
    });
    let npsScore = null;
    if (props.showNpsScore) {
      const promoters = segments.filter((s) => s.code === "promoters").reduce((sum, s) => sum + s.value, 0);
      const detractors = segments.filter((s) => s.code === "detractors").reduce((sum, s) => sum + s.value, 0);
      npsScore = Math.round((promoters - detractors) * 100);
    }
    return {
      itemLabel: row.itemLabel,
      groupLabel: row.groupLabel,
      itemN: row.itemN,
      questionId: row.questionId,
      popId: row.popId,
      popColor: row.popColor,
      segments,
      npsScore,
    };
  }),
);

/**
 * Group rows by groupLabel for matrix + multi-population rendering.
 * Returns [{ groupLabel: string|null, rows: row[] }]
 */
const groupedRows = computed(() => {
  const groups = [];
  let current = null;
  for (const row of rows.value) {
    if (row.groupLabel && row.groupLabel !== current?.groupLabel) {
      current = { groupLabel: row.groupLabel, rows: [] };
      groups.push(current);
    } else if (!row.groupLabel && current?.groupLabel !== null) {
      // Group all ungrouped rows together in one group
      current = { groupLabel: null, rows: [] };
      groups.push(current);
    }
    current.rows.push(row);
  }
  return groups;
});

const hasGroups = computed(() => groupedRows.value.some((g) => g.groupLabel));

// The legend shows the first row's colors, unless the rows come in several palettes
// (one population each): a neutral ramp then keeps "darker = first" without picking a side.
const legend = computed(() => {
  const first = rows.value[0];
  if (!first) return [];
  const onePalette = rows.value.every((row) => row.popColor === first.popColor);
  if (onePalette) return first.segments;
  const neutral = spreadShades(POPULATION_COLOR_PALETTES.slate, first.segments.length);
  return first.segments.map((seg, i) => ({ ...seg, color: seg.code === "999" ? seg.color : neutral[i] }));
});

function sigIcon(seg) {
  if (seg.significance === "+") return "up";
  if (seg.significance === "-") return "down";
  return null;
}

function tooltipTitle(seg, row) {
  if (!seg.scores || !row.itemLabel) return seg.label;
  return `${seg.label} — ${row.itemLabel}`;
}

function sigDetailsText(seg) {
  const d = seg.significanceDetails;
  if (!d) return null;
  const parts = [];
  if (d.superiorTo?.length) parts.push(`Superior to: ${d.superiorTo.join(", ")}`);
  if (d.inferiorTo?.length) parts.push(`Inferior to: ${d.inferiorTo.join(", ")}`);
  return parts.length ? parts.join(" · ") : null;
}
</script>

<template>
  <div v-if="data.length && choiceCodes.length" class="flex flex-col gap-3">
    <!-- Horizontal stacked bars -->
    <div v-if="horizontal" class="flex flex-col" :class="hasGroups ? 'gap-4' : 'gap-3'">
      <div
        v-for="(group, gIdx) in groupedRows"
        :key="gIdx"
        class="flex flex-col gap-1.5"
      >
        <!-- Group header (matrix item name) -->
        <span
          v-if="group.groupLabel"
          class="text-xs font-semibold text-foreground"
          v-html="renderMarkdownInline(group.groupLabel)"
        />

        <!-- Rows within the group -->
        <div
          v-for="(row, rowIdx) in group.rows"
          :key="rowIdx"
          class="flex flex-col gap-0.5"
        >
          <span class="text-xs leading-5 font-medium break-words">
            <span v-html="renderMarkdownInline(row.itemLabel)" />
            <span v-if="row.itemN != null" class="font-normal text-muted-foreground"> (N={{ row.itemN }})</span>
          </span>
          <div class="flex items-center gap-2">
          <div class="flex-1 min-w-0 h-8 rounded overflow-hidden flex">
            <Tooltip v-for="seg in row.segments" :key="seg.code" :delay-duration="0">
              <div
                class="h-full flex items-center justify-center transition-all duration-300 relative"
                :class="clickable && seg.code !== '999' ? 'cursor-pointer' : ''"
                :style="[{ width: segSize(seg), backgroundColor: seg.color }, revealDelayStyle(rowIdx)]"
                @click="onSegClick($event, seg, row)"
              >
                <span
                  v-if="seg.value >= 0.06"
                  class="text-3xs font-medium tabular-nums text-white drop-shadow-sm flex items-center gap-0.5"
                >
                  <AnimatedPercent :value="seg.value" :delay="revealDelay(rowIdx)" />
                  <span
                    v-if="showSignificance && sigIcon(seg) === 'up'"
                    class="text-4xs text-white"
                  >▲</span>
                  <span
                    v-if="showSignificance && sigIcon(seg) === 'down'"
                    class="text-4xs text-white"
                  >▼</span>
                </span>
              </div>
              <template #content>
                <div class="font-medium mb-1" v-html="renderMarkdownInline(tooltipTitle(seg, row))" />
                <div v-if="seg.scores" class="grid gap-1">
                  <div
                    v-for="s in seg.scores"
                    :key="s.code"
                    class="flex items-center justify-between gap-4"
                  >
                    <span>{{ s.code }}</span>
                    <span class="font-medium tabular-nums">
                      {{ formatPercent(s.value) }}
                      <span class="font-normal">(n={{ s.count }})</span>
                    </span>
                  </div>
                </div>
                <div v-else class="grid gap-1">
                  <div
                    v-for="r in group.rows"
                    :key="r.itemLabel"
                    class="flex items-center justify-between gap-4"
                  >
                    <div class="flex items-center gap-1.5">
                      <div class="h-2.5 w-2.5 shrink-0 rounded-xs" :style="{ backgroundColor: r.segments.find((s) => s.code === seg.code)?.color }" />
                      <span v-html="renderMarkdownInline(r.itemLabel)" />
                    </div>
                    <span class="font-medium tabular-nums flex items-center gap-1">
                      {{ formatPercent(r.segments.find((s) => s.code === seg.code)?.value || 0) }}
                      <span class="font-normal">(n={{ r.segments.find((s) => s.code === seg.code)?.count ?? 0 }})</span>
                      <span
                        v-if="showSignificance && sigIcon(r.segments.find((s) => s.code === seg.code) || {}) === 'up'"
                        class="text-3xs text-status-success"
                      >▲</span>
                      <span
                        v-if="showSignificance && sigIcon(r.segments.find((s) => s.code === seg.code) || {}) === 'down'"
                        class="text-3xs text-status-error"
                      >▼</span>
                    </span>
                  </div>
                  <template v-if="showSignificance">
                    <div
                      v-for="r in group.rows"
                      :key="`sig-${r.itemLabel}`"
                    >
                      <div
                        v-if="sigDetailsText(r.segments.find((s) => s.code === seg.code) || {})"
                        class="text-3xs mt-0.5"
                      >
                        <span class="inline-block h-2.5 w-2.5 shrink-0 rounded-xs mr-1" :style="{ backgroundColor: r.segments.find((s) => s.code === seg.code)?.color }" />
                        {{ sigDetailsText(r.segments.find((s) => s.code === seg.code) || {}) }}
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </Tooltip>
          </div>
          <span
            v-if="showNpsScore && row.npsScore !== null"
            class="shrink-0 w-16 text-right text-xs font-semibold tabular-nums text-foreground"
          >NPS {{ row.npsScore > 0 ? '+' : '' }}{{ row.npsScore }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Vertical stacked bars -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="(group, gIdx) in groupedRows"
        :key="gIdx"
        class="flex flex-col items-center-safe gap-1"
      >
        <span
          v-if="group.groupLabel"
          class="text-xs font-semibold text-foreground text-center"
          v-html="renderMarkdownInline(group.groupLabel)"
        />

        <div class="flex items-end justify-center-safe gap-2">
          <div
            v-for="(row, rowIdx) in group.rows"
            :key="rowIdx"
            class="flex flex-col items-center gap-1"
          >
            <div class="w-12 h-48 rounded overflow-hidden flex flex-col-reverse">
              <Tooltip v-for="seg in row.segments" :key="seg.code" :delay-duration="0">
                <div
                  class="w-full flex items-center justify-center transition-all duration-300"
                  :class="clickable && seg.code !== '999' ? 'cursor-pointer' : ''"
                  :style="[{ height: segSize(seg), backgroundColor: seg.color }, revealDelayStyle(rowIdx)]"
                  @click="onSegClick($event, seg, row)"
                >
                  <span
                    v-if="seg.value >= 0.08"
                    class="text-3xs font-medium tabular-nums text-white drop-shadow-sm flex items-center gap-0.5"
                  >
                    <AnimatedPercent :value="seg.value" :delay="revealDelay(rowIdx)" />
                    <span
                      v-if="showSignificance && sigIcon(seg) === 'up'"
                      class="text-4xs text-white"
                    >▲</span>
                    <span
                      v-if="showSignificance && sigIcon(seg) === 'down'"
                      class="text-4xs text-white"
                    >▼</span>
                  </span>
                </div>
                <template #content>
                  <div class="font-medium mb-1" v-html="renderMarkdownInline(tooltipTitle(seg, row))" />
                  <div v-if="seg.scores" class="grid gap-1">
                    <div
                      v-for="s in seg.scores"
                      :key="s.code"
                      class="flex items-center justify-between gap-4"
                    >
                      <span>{{ s.code }}</span>
                      <span class="font-medium tabular-nums">
                        {{ formatPercent(s.value) }}
                        <span class="font-normal">(n={{ s.count }})</span>
                      </span>
                    </div>
                  </div>
                  <div v-else class="grid gap-1">
                    <div
                      v-for="r in group.rows"
                      :key="r.itemLabel"
                      class="flex items-center justify-between gap-4"
                    >
                      <div class="flex items-center gap-1.5">
                        <div class="h-2.5 w-2.5 shrink-0 rounded-xs" :style="{ backgroundColor: r.segments.find((s) => s.code === seg.code)?.color }" />
                        <span v-html="renderMarkdownInline(r.itemLabel)" />
                      </div>
                      <span class="font-medium tabular-nums flex items-center gap-1">
                        {{ formatPercent(r.segments.find((s) => s.code === seg.code)?.value || 0) }}
                        <span class="font-normal">(n={{ r.segments.find((s) => s.code === seg.code)?.count ?? 0 }})</span>
                        <span
                          v-if="showSignificance && sigIcon(r.segments.find((s) => s.code === seg.code) || {}) === 'up'"
                          class="text-3xs text-status-success"
                        >▲</span>
                        <span
                          v-if="showSignificance && sigIcon(r.segments.find((s) => s.code === seg.code) || {}) === 'down'"
                          class="text-3xs text-status-error"
                        >▼</span>
                      </span>
                    </div>
                  </div>
                </template>
              </Tooltip>
            </div>
            <span class="text-3xs leading-tight font-medium text-center w-14 truncate">
              <span v-html="renderMarkdownInline(row.itemLabel)" />
              <span v-if="row.itemN != null" class="font-normal text-muted-foreground"> (N={{ row.itemN }})</span>
            </span>
            <span
              v-if="showNpsScore && row.npsScore !== null"
              class="text-3xs font-semibold tabular-nums text-foreground"
            >NPS {{ row.npsScore > 0 ? '+' : '' }}{{ row.npsScore }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div v-if="legend.length" class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs pt-1">
      <div
        v-for="seg in legend"
        :key="seg.code"
        class="flex items-center gap-1.5"
      >
        <div class="h-2 w-2 shrink-0 rounded-xs" :style="{ backgroundColor: seg.color }" />
        <span class="text-muted-foreground" v-html="renderMarkdownInline(seg.label)" />
      </div>
    </div>
  </div>
</template>
